import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { Icons } from './Icons';

export { Icons };

/* ── Spinner ─────────────────────────────────────────────────────────────── */
export function Spinner({ text = 'Loading…' }) {
  return (
    <div className="loader-wrap">
      <div className="spinner" />
      <span className="loader-text">{text}</span>
    </div>
  );
}

/* ── Empty state ─────────────────────────────────────────────────────────── */
export function Empty({ msg = 'No records found', desc = 'Add a new entry to get started.' }) {
  return (
    <div className="empty-state">
      <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M3 7h18M3 12h18M3 17h18" strokeLinecap="round"/>
      </svg>
      <div className="empty-title">{msg}</div>
      <div className="empty-desc">{desc}</div>
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────────────────────────── */
export function Modal({ title, onClose, children, footer, size = '' }) {
  useEffect(() => {
    const esc = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size}`}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="icon-btn" onClick={onClose} title="Close">
            <Icons.Close size={15} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ── Badge ───────────────────────────────────────────────────────────────── */
export function Badge({ status }) {
  if (!status) return null;
  return <span className={`badge badge-${status.toLowerCase()}`}>{status.replace('_', ' ')}</span>;
}

/* ── Page header ─────────────────────────────────────────────────────────── */
export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="page-header">
      <div className="page-header-text">
        <h2 className="page-title">{title}</h2>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

/* ── Field ───────────────────────────────────────────────────────────────── */
export function Field({ label, required, children }) {
  return (
    <div className="form-group">
      {label && <label className={required ? 'required' : ''}>{label}</label>}
      {children}
    </div>
  );
}

/* ── Section label ───────────────────────────────────────────────────────── */
export function SectionLabel({ children }) {
  return <div className="section-label">{children}</div>;
}

/* ── Icon action buttons ─────────────────────────────────────────────────── */
export function EditBtn({ onClick, label = 'Edit' }) {
  return (
    <button className="icon-btn" onClick={onClick} title={label}>
      <Icons.Edit size={14} />
    </button>
  );
}

export function CancelBtn({ onClick, label = 'Deactivate' }) {
  return (
    <button className="icon-btn danger" onClick={onClick} title={label}>
      <Icons.Cancel size={14} />
    </button>
  );
}

export function ReturnBtn({ onClick }) {
  return (
    <button className="icon-btn danger" onClick={onClick} title="Return">
      <Icons.Return size={14} />
    </button>
  );
}

export function PdfBtn({ href }) {
  const handleClick = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(href, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Failed to load PDF (${res.status})`);
      }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || 'Failed to open PDF');
    }
  };
  return (
    <button className="icon-btn" onClick={handleClick} title="View PDF">
      <Icons.PDF size={14} />
    </button>
  );
}

export function DeleteBtn({ onClick }) {
  return (
    <button className="icon-btn danger" onClick={onClick} title="Delete">
      <Icons.Trash size={14} />
    </button>
  );
}

/* ── Image preview ───────────────────────────────────────────────────────── */
export function ImgPreview({ src, base, small }) {
  if (!src) return null;
  const url = src.startsWith('http') ? src : `${base || '/uploads'}/${src}`;
  return <img src={url} alt="" className={small ? 'img-preview img-preview-sm' : 'img-preview'} />;
}

/* ── Confirm ─────────────────────────────────────────────────────────────── */
export function Confirm({ message, onConfirm, onCancel, danger = true }) {
  return (
    <Modal title="Confirm Action" onClose={onCancel}
      footer={<>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>Confirm</button>
      </>}>
      <div className="flex-center gap-12" style={{ padding: '8px 0' }}>
        <Icons.AlertTriangle size={20} style={{ color: 'var(--c-warning)', flexShrink: 0 }} />
        <p style={{ fontSize: '.9rem', color: 'var(--c-text-2)', lineHeight: 1.6 }}>{message}</p>
      </div>
    </Modal>
  );
}
